/**
 * Visibility пунктов inline-меню таблицы после onInlineMenuShow.
 * Порядок как в ElementRenderer.isTableInlineActionVisible:
 * snapshot меню → deferred setVisible("action",…) → registry MAction → true.
 */
export function resolveTableInlineActionVisible(options: {
  fromMenu?: boolean
  deferred?: boolean
  registryVisible?: boolean
}): boolean {
  if (options.fromMenu !== undefined) return options.fromMenu
  if (options.deferred !== undefined) return options.deferred
  if (options.registryVisible !== undefined) return options.registryVisible
  return true
}

/** Ids layout.position=inline для сброса stale deferred перед onInlineMenuShow. */
export function collectInlineActionIds(actions: Array<{ _attributes?: { id?: unknown }; id?: unknown }>): string[] {
  const ids: string[] = []
  for (const action of actions) {
    const actionId = String(action?._attributes?.id ?? action?.id ?? '').trim()
    if (actionId) ids.push(actionId)
  }
  return ids
}

/**
 * Собирает map visibility из deferred peek для текущего списка inline-actions.
 * Вызывается на каждый show меню — без «залипания» предыдущего false.
 */
export function buildTableInlineActionVisibilityMap(
  actionIds: Array<string | number>,
  peekDeferred: (actionId: string | number) => boolean | undefined,
): Record<string, boolean> {
  const next: Record<string, boolean> = {}
  for (const rawId of actionIds) {
    const actionId = String(rawId ?? '').trim()
    if (!actionId) continue
    const deferred = peekDeferred(actionId)
    if (deferred !== undefined) {
      next[actionId] = deferred
    }
  }
  return next
}

function actionNodeName(action: unknown): string {
  if (!action || typeof action !== 'object') return ''
  const node = action as { name?: { _text?: unknown } | string }
  if (typeof node.name === 'string') return node.name.trim()
  return String(node.name?._text ?? '').trim()
}

function actionNodeId(action: unknown): string {
  if (!action || typeof action !== 'object') return ''
  const node = action as { _attributes?: { id?: unknown }; id?: unknown }
  return String(node._attributes?.id ?? node.id ?? '').trim()
}

/**
 * Exact action `name` → xml id. `goToBannerBtn` must not match `goToBannerBtn__newOrder`.
 * Inline actions are often not mounted MAction widgets, so setVisible(name) has nothing to call.
 */
export function collectActionIdsByExactName(actions: readonly unknown[], name: string): string[] {
  const wanted = String(name ?? '').trim()
  if (!wanted) return []
  const ids: string[] = []
  for (const action of actions) {
    if (actionNodeName(action) !== wanted) continue
    const id = actionNodeId(action)
    if (id) ids.push(id)
  }
  return ids
}

/** Writes deferred visibility for every form action with this exact name. */
export function rememberNamedFormActionVisibility(
  actions: readonly unknown[],
  name: string,
  visible: boolean,
  remember: (actionId: string, visible: boolean) => void,
): boolean {
  const ids = collectActionIdsByExactName(actions, name)
  if (ids.length === 0) return false
  for (const id of ids) remember(id, visible)
  return true
}
